import constants from 'core/data-model/constants/defaults'
import messages from 'core/data-model/constants/messages'
import MESSAGES from 'core/data-model/constants/validation-messages'
import { isPreAdult, isUnderage } from 'core/service/insured/age'
import { isValidAge } from 'core/service/insured/birthdate'
import _ from 'lodash'
import { get } from 'lodash/fp'
import { getToggles } from 'quick-quote/feature-toggles'
import { isInvestmentCategory } from 'quick-quote/product-selection/selectors'
import { isQQFlow } from 'quick-quote/portal/selectors'
import { isAllowReviseQQMenu } from 'identity/selectors'
import { createSelector } from 'reselect'

export const getOccupations = (state) => state.occupations.payload
export const getOccupationsStatus = (state) => state.occupations.status
export const getGenderOptions = () => [
  {
    text: messages.MALE,
    value: constants.MALE,
  },
  {
    text: messages.FEMALE,
    value: constants.FEMALE,
  },
]

export const getPayerRelation = (state) => state.insured.payer.relation
export const getPayerRelationOptions = (state) => {
  const checkCanSelectablePayerRelation = (state, value) => {
    if (!!state.selectedDisplayProduct.payerSelection) {
      const payerSelection = get('selectedDisplayProduct.payerSelection')(state)
      const insureAge = getAge(state)
      const isInsuredValidAge = isValidAge(
        insureAge.value,
        insureAge.unit,
        constants.DEFAULT_PAYER_RELATION_ENTRY_AGE[value]
      )
      return payerSelection.includes(value) && isInsuredValidAge
    } else {
      return false
    }
  }

  return [
    {
      text: messages.PAY_MY_SELF,
      value: constants.PAY_MY_SELF,
      canSelectable: checkCanSelectablePayerRelation(state, constants.PAY_MY_SELF),
    },
    {
      text: messages.PARENT,
      value: constants.PARENT,
      canSelectable: checkCanSelectablePayerRelation(state, constants.PARENT),
    },
    {
      text: messages.SPOUSE,
      value: constants.SPOUSE,
      canSelectable: checkCanSelectablePayerRelation(state, constants.SPOUSE),
    },
  ]
}

export const isShowPayerRelationSelection = (state) =>
  getToggles().ENABLE_NEW_PAYER_SELECTION &&
  !!state.selectedDisplayProduct.payerSelection &&
  !_.isEmpty(get('selectedDisplayProduct.payerSelection')(state))

export const isNeedPayerComponent = (state) => {
  if (isShowPayerRelationSelection(state)) return true
  else return state.insured.payerNeeded
}

export const getGender = (state) => state.insured.gender
export const getBirthdate = (state) => get('insured.birthdate.value')(state)
export const getIsValidBirthdate = (state) => _.isEmpty(get('insured.birthdate.errors')(state))
export const getAge = (state) => state.insured.age
export const getErrorBirthdateMessage = (state) => state.insured.birthdate.errors.join('\n')
export const getBirthdateErrors = (state) => state.insured.birthdate.errors
export const getSelectedOccupationCode = (state) => state.insured.occupation.code
export const getSelectedNatureOfDutyCode = (state) => state.insured.occupation.natureOfDuty.code
export const getInsuredOccupationErrors = (state) => state.insured.occupation.errors
export const getInsuredOccupationClass = (state) => state.insured.occupation.natureOfDuty.occupationClass

export const getInsuredFirstName = (state) => state.insured.firstName.name
export const getInsuredLastName = (state) => state.insured.lastName.name
export const getInsuredCitizenId = (state) => state.insured.idNo.citizenId
export const getInsuredPassport = (state) => state.insured.foreigner?.passport
export const getInsuredIdType = (state) => (state.insured.idType ? state.insured.idType.value : '')
export const getQuickQuoteInsured = (state) => {
  return state?.insured
}

export const getPayerNeeded = (state) => state.insured.payerNeeded
export const getSelectedPayerOccupationCode = (state) => state.insured.payer.occupation.code
export const getSelectedPayerNatureOfDutyCode = (state) => get('insured.payer.occupation.natureOfDuty.code')(state)
export const getPayerOccupationErrors = (state) => get('insured.payer.occupation.errors')(state)
export const getPayerBirthdate = (state) => state.insured.payer.birthdate.value
export const getPayerIsValidBirthdate = (state) => _.isEmpty(state.insured.payer.birthdate.errors)
export const getPayerAge = (state) => state.insured.payer.age
export const getPayerGender = (state) => state.insured.payer.gender
export const getErrorPayerBirthdateMessage = (state) => state.insured.payer.birthdate.errors.join('\n')
export const getPayerBirthdateErrors = (state) => state.insured.payer.birthdate.errors

const findOccupation = (occupations, selectedOccupationCode, selectedNatureOfDutyCode) => {
  return _.find(occupations, {
    occupationCode: selectedOccupationCode,
    natureOfDutyCode: selectedNatureOfDutyCode,
  })
}

export const getSelectedOccupation = createSelector(
  getOccupations,
  getSelectedOccupationCode,
  getSelectedNatureOfDutyCode,
  findOccupation
)

export const getSelectedPayerOccupation = createSelector(
  getOccupations,
  getSelectedPayerOccupationCode,
  getSelectedPayerNatureOfDutyCode,
  findOccupation
)

export const getSelectedPayerOtherNatureOfDutyCode = (state) =>
  _.get(state, 'insured.payer.otherOccupation.natureOfDuty.code')
export const getSelectedPayerOtherOccupationCode = (state) => _.get(state, 'insured.payer.otherOccupation.code')
export const getIsSelectedPayerOtherOccupation = (state) =>
  _.get(state, 'insured.payer.otherOccupation.isSelected', false)
export const getPayerOtherOccupationErrors = (state) => state.insured.payer.otherOccupation.errors
export const getSelectedPayerOtherOccupation = createSelector(
  getOccupations,
  getSelectedPayerOtherOccupationCode,
  getSelectedPayerOtherNatureOfDutyCode,
  findOccupation
)

export const getIsSelectedOtherOccupation = (state) => state.insured.otherOccupation.isSelected
export const getInsuredOtherOccupationErrors = (state) => state.insured.otherOccupation.errors
export const getSelectedOtherOccupationCode = (state) => state.insured.otherOccupation.code
export const getSelectedOtherNatureOfDutyCode = (state) => state.insured.otherOccupation.natureOfDuty.code
export const getSelectedOtherOccupation = createSelector(
  getOccupations,
  getSelectedOtherOccupationCode,
  getSelectedOtherNatureOfDutyCode,
  findOccupation
)

export const getAllNatureOfDutyCodes = createSelector(
  getSelectedNatureOfDutyCode,
  getSelectedOtherNatureOfDutyCode,
  getIsSelectedOtherOccupation,
  (natureOfDuty, otherNatureOfDuty, isSelectedOtherOccupation) => {
    return [natureOfDuty]
  }
)

export const getAllPayerNatureOfDutyCodes = createSelector(
  getSelectedPayerNatureOfDutyCode,
  getSelectedPayerOtherNatureOfDutyCode,
  getIsSelectedPayerOtherOccupation,
  (payerNatureOfDuty, payerOtherNatureOfDuty, isSelectedPayerOtherOccupation) => {
    const natureOfDuty = isSelectedPayerOtherOccupation
      ? [payerNatureOfDuty, payerOtherNatureOfDuty]
      : [payerNatureOfDuty]
    return natureOfDuty
  }
)

export const getIsInsuredOccupationNotEmpty = createSelector(
  getSelectedOccupation,
  getIsSelectedOtherOccupation,
  getSelectedOtherOccupation,
  (insuredOccupation, isInsuredHasOtherOccupation, insureOtherOccupation) => {
    const isValidInsuredOccupation = !_.isUndefined(insuredOccupation)
    const isValidInsuredOtherOccupation = isInsuredHasOtherOccupation ? !_.isUndefined(insureOtherOccupation) : true

    return isValidInsuredOccupation && isValidInsuredOtherOccupation
  }
)
export const getIsOccupationsNotEmpty = createSelector(
  getIsInsuredOccupationNotEmpty,
  getPayerNeeded,
  getSelectedPayerOccupation,
  getIsSelectedPayerOtherOccupation,
  getSelectedPayerOtherOccupation,
  isInvestmentCategory,
  (
    isInsuredOccupationNotEmpty,
    needPayer,
    payerOccupation,
    isPayerHasOtherOccupation,
    payerOtheOccupation,
    isInvestmentCategory
  ) => {
    const isValidPayerOccupation = needPayer && !isInvestmentCategory ? !_.isUndefined(payerOccupation) : true
    const isValidPayerOtherOccupation =
      needPayer && isPayerHasOtherOccupation && !isInvestmentCategory ? !_.isUndefined(payerOtheOccupation) : true

    return isInsuredOccupationNotEmpty && isValidPayerOccupation && isValidPayerOtherOccupation
  }
)

// $FlowFixMe
export const getInsured = (state) => ({
  occupation: getSelectedOccupation(state),
  otherOccupation: getSelectedOtherOccupation(state),
  gender: getGender(state),
  age: getAge(state),
})

// $FlowFixMe
export const getPayer = (state) => ({
  occupation: getSelectedPayerOccupation(state),
  gender: getPayerGender(state),
  age: getPayerAge(state),
  birthdate: getPayerBirthdate(state),
})

export const getGenderLabel = createSelector(getGenderOptions, getGender, (genderOptions, gender) =>
  _.get(_.find(genderOptions, { value: gender }), 'text')
)

const filterNatureOfDuties = (occupations, selectedOccupationCode) => {
  const natureOfDuties = occupations
    .filter((occupation) => occupation.occupationCode === selectedOccupationCode)
    .sort((a, b) => a.th.sortOrder - b.th.sortOrder)
    .map((occupation) => ({
      code: occupation.natureOfDutyCode,
      text: occupation.th.natureOfDuty,
      occupationClass: occupation.occupationClass,
    }))
  return natureOfDuties
}

const filterUnderage = (age, natureOfDuties) => {
  if (isUnderage(age)) {
    if (isPreAdult(age)) {
      return natureOfDuties.filter((occupation) => occupation.code !== constants.DEFAULT_UNDERAGE_NATURE_OF_DUTY_CODE)
    } else {
      return _.filter(
        natureOfDuties,
        (occupation) => !_.includes(constants.IGNORE_NATURE_OF_DUTY_CODE_BETWEEN_AGE_18_19, occupation.code)
      )
    }
  } else {
    return natureOfDuties
  }
}

export const getDefaultOccupationCode = createSelector(getAge, (age) => {
  return isUnderage(age) ? constants.DEFAULT_SELECT_OCCUPATION_CODE_KIDS : constants.DEFAULT_SELECT_OCCUPATION_CODE
})

export const getDefaultPayerOccupationCode = (state) => constants.DEFAULT_SELECT_OCCUPATION_CODE

export const getPayerOtherNatureOfDuties = createSelector(
  getOccupations,
  getSelectedPayerOtherOccupationCode,
  getDefaultPayerOccupationCode,
  (occupations, selectedPayerOtherOccupationCode, defaultOccupationCode) => {
    const payerOtherOccupationCode =
      _.isEmpty(selectedPayerOtherOccupationCode) && getToggles().ENABLE_OCCUPATION_BLANK
        ? defaultOccupationCode
        : selectedPayerOtherOccupationCode
    return filterNatureOfDuties(occupations, payerOtherOccupationCode)
  }
)

export const getOtherNatureOfDuties = createSelector(
  getOccupations,
  getSelectedOtherOccupationCode,
  getDefaultOccupationCode,
  (occupations, selectedOtherOccupationCode, defaultOccupationCode) => {
    const payerOccupationCode =
      _.isEmpty(selectedOtherOccupationCode) && getToggles().ENABLE_OCCUPATION_BLANK
        ? defaultOccupationCode
        : selectedOtherOccupationCode
    return filterNatureOfDuties(occupations, payerOccupationCode)
  }
)

export const getPayerNatureOfDuties = createSelector(
  getOccupations,
  getSelectedPayerOccupationCode,
  getDefaultPayerOccupationCode,
  (occupations, selectedPayerOccupationCode, defaultOccupationCode) => {
    const payerOccupationCode =
      _.isEmpty(selectedPayerOccupationCode) && getToggles().ENABLE_OCCUPATION_BLANK
        ? defaultOccupationCode
        : selectedPayerOccupationCode
    return filterNatureOfDuties(occupations, payerOccupationCode)
  }
)

export const getDefaultPayerNatureOfDuty = createSelector(getPayerNatureOfDuties, (natureOfDuties) => {
  const [defaultNatureOfDuty] = natureOfDuties
  return defaultNatureOfDuty
})

export const getDefaultNatureOfDuries = createSelector(
  getOccupations,
  getDefaultOccupationCode,
  getAge,
  (occupations, occupationCode, age) => {
    const natureOfDuties = filterNatureOfDuties(occupations, occupationCode)
    return filterUnderage(age, natureOfDuties)
  }
)

export const getNatureOfDuties = createSelector(
  getOccupations,
  getSelectedOccupationCode,
  getAge,
  getDefaultOccupationCode,
  (occupations, selectedOccupationCode, age, defaultOccupationCode) => {
    const occupationCode =
      _.isEmpty(selectedOccupationCode) && getToggles().ENABLE_OCCUPATION_BLANK
        ? defaultOccupationCode
        : selectedOccupationCode
    const filtered = filterNatureOfDuties(occupations, occupationCode)
    return filterUnderage(age, filtered)
  }
)

export const getUniqueOccupations = createSelector(getOccupations, (occupations) => {
  const uniqueOccupations = _(occupations)
    .sortBy('th.sortOrder')
    .map(({ th, en, occupationCode }) => ({
      th: { occupationName: th.occupationName, sortOrder: th.sortOrder },
      en: { occupationName: en.occupationName, sortOrder: en.sortOrder },
      occupationCode,
    }))
    .uniqBy('th.occupationName')
    .value()

  return uniqueOccupations
})
export const getTitleErrors = (state) =>
  !getToggles().ENABLE_QQ_INSURED_TITLE ||
  (getToggles().ENABLE_QQ_INSURED_TITLE && isAllowReviseQQMenu(state) && isQQFlow(state))
    ? []
    : _.get(state, 'insured.title.errors', [])
export const getFirstnameErrors = (state) => state.insured.firstName.errors
export const getLastnameErrors = (state) => state.insured.lastName.errors
export const isOccupationRisk = (state) => {
  const haveErrors = state.insured.occupation.errors.length > 0
  return haveErrors
    ? [
        MESSAGES.RULE_INSURED_PRODUCT_OCCUPATION_FACTOR,
        MESSAGES.RULE_INSURED_OCCUPATION_FACTOR,
        MESSAGES.RULE_OCCUPATION_FACTOR,
      ].includes(state.insured.occupation.errors[0])
    : false
}

export const _hasFinishedInsuredInformation = (
  isPayerNeeded,
  birthdateErrors,
  payerBirthdateErrors,
  titleErrors,
  firstnameErrors,
  lastnameErrors,
  isInsuredOccupationNotEmpty,
  _isOccupationRisk
) => {
  const errors = isPayerNeeded
    ? [...birthdateErrors, ...payerBirthdateErrors, ...firstnameErrors, ...lastnameErrors]
    : [...birthdateErrors, ...titleErrors, ...firstnameErrors, ...lastnameErrors]

  return errors.length === 0 && isInsuredOccupationNotEmpty && !_isOccupationRisk
}

export const hasFinishedInsuredInformation = createSelector(
  getPayerNeeded,
  getBirthdateErrors,
  getPayerBirthdateErrors,
  getTitleErrors,
  getFirstnameErrors,
  getLastnameErrors,
  getIsInsuredOccupationNotEmpty,
  isOccupationRisk,
  _hasFinishedInsuredInformation
)

export const hasInputAllInsuredInformationWithoutErrors = createSelector(
  hasFinishedInsuredInformation,
  getBirthdate,
  getIsValidBirthdate,
  getPayerNeeded,
  getPayerBirthdate,
  getPayerIsValidBirthdate,
  (isFinishedInsuredInformation, birthdate, isValidBirthdate, needPayer, payerBirthdaye, isValidPayerBirthdate) => {
    const validInsuredBirthdate = birthdate && isValidBirthdate
    const validPayerBirthdate = needPayer ? payerBirthdaye && isValidPayerBirthdate : true
    return isFinishedInsuredInformation && validInsuredBirthdate && validPayerBirthdate
  }
)
